import React, { useState, useEffect } from "react";
import {
  X,
  Edit3,
  Building,
  Clock,
  Users,
  CheckSquare,
  Square,
  AlertCircle,
} from "lucide-react";
import axiosInstance from "../../../../axiosInstance"; // Make sure this path is correct
import { toast } from "react-toastify"; // Added for better user feedback

const BulkUpdateModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedCount,
  selectedShifts,
  companyId,
}) => {
  // Internal state for branches
  const [branches, setBranches] = useState([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const [updateData, setUpdateData] = useState({
    shiftName: "",
    shiftForm: "",
    shiftTo: "",
    offTime: "",
    status: "",
    branchIds: [],
    updateShiftName: false,
    updateTimes: false,
    updateOffTime: false,
    updateStatus: false,
    updateBranches: false,
    selectAllBranches: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // This effect now handles both form reset and data fetching
    if (isOpen) {
      // Reset form state when modal opens
      setUpdateData({
        shiftName: "",
        shiftForm: "",
        shiftTo: "",
        offTime: "",
        status: "",
        branchIds: [],
        updateShiftName: false,
        updateTimes: false,
        updateOffTime: false,
        updateStatus: false,
        updateBranches: false,
        selectAllBranches: false,
      });

      // Fetch enabled branches from the new endpoint
      const fetchEnabledBranches = async () => {
        setIsLoadingBranches(true);
        try {
          const response = await axiosInstance.get(
            `${process.env.REACT_APP_API_BASE_URL}/branches/enabled/list-with-departments`
          );

          // Correctly check the status and the presence of the data array
          if (
            response.data &&
            response.data.status === 200 &&
            response.data.data
          ) {
            setBranches(response.data.data || []);
          } else {
            toast.error(response.data.message || "Failed to load branches.");
            setBranches([]);
          }
        } catch (error) {
          toast.error("An error occurred while fetching branches.");
          console.error("Error fetching branches:", error);
          setBranches([]);
        } finally {
          setIsLoadingBranches(false);
        }
      };
      fetchEnabledBranches();
    }
  }, [isOpen]);

  const handleChange = (field, value) => {
    setUpdateData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBranchSelect = (branchId) => {
    setUpdateData((prev) => {
      const newBranchIds = prev.branchIds.includes(branchId)
        ? prev.branchIds.filter((id) => id !== branchId)
        : [...prev.branchIds, branchId];
      const selectAllBranches =
        branches.length > 0 && newBranchIds.length === branches.length;
      return { ...prev, branchIds: newBranchIds, selectAllBranches };
    });
  };

  const handleSelectAllBranches = () => {
    const allBranchIds = branches.map((branch) => branch.branchId);
    if (updateData.selectAllBranches) {
      setUpdateData((prev) => ({
        ...prev,
        branchIds: [],
        selectAllBranches: false,
      }));
    } else {
      setUpdateData((prev) => ({
        ...prev,
        branchIds: allBranchIds,
        selectAllBranches: true,
      }));
    }
  };

  const calculateOffTime = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);
    const start = new Date(0, 0, 0, startHour, startMinute, 0);
    const end = new Date(0, 0, 0, endHour, endMinute, 0);
    let diffMs = end - start;
    if (diffMs < 0) {
      diffMs += 24 * 60 * 60 * 1000;
    }
    return diffMs / (1000 * 60 * 60);
  };

  const convertTimeToApiFormat = (timeString) => {
    if (!timeString) return null;
    const [hour, minute] = timeString.split(":").map(Number);
    return { hour, minute, second: 0, nano: 0 };
  };

  useEffect(() => {
    if (updateData.updateTimes && updateData.shiftForm && updateData.shiftTo) {
      const hours = calculateOffTime(updateData.shiftForm, updateData.shiftTo);
      const workingHours = Math.max(hours - 1, 0);
      setUpdateData((prev) => ({
        ...prev,
        offTime: workingHours.toFixed(2),
        updateOffTime: true,
      }));
    }
  }, [updateData.shiftForm, updateData.shiftTo, updateData.updateTimes]);

  const hasUpdates =
    updateData.updateShiftName ||
    updateData.updateTimes ||
    updateData.updateOffTime ||
    updateData.updateStatus ||
    updateData.updateBranches;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasUpdates)
      return toast.warn("Please select at least one field to update.");
    if (updateData.updateShiftName && !updateData.shiftName.trim())
      return toast.error("Shift Name cannot be empty.");
    if (
      updateData.updateTimes &&
      (!updateData.shiftForm || !updateData.shiftTo)
    )
      return toast.error("Both Start and End times are required.");
    if (updateData.updateBranches && updateData.branchIds.length === 0)
      return toast.error("Please select at least one branch.");

    setIsSubmitting(true);
    try {
      const requestPayload = selectedShifts.map((shift) => {
        const updateRecord = {
          officeTimingId: shift.officeTimingId,
          companyId: process.env.REACT_APP_COMPANY_ID,
        };
        if (updateData.updateShiftName)
          updateRecord.shiftName = updateData.shiftName;
        if (updateData.updateTimes) {
          updateRecord.shiftForm = convertTimeToApiFormat(updateData.shiftForm);
          updateRecord.shiftTo = convertTimeToApiFormat(updateData.shiftTo);
        }
        if (updateData.updateOffTime)
          updateRecord.offTime = parseFloat(updateData.offTime);
        if (updateData.updateStatus) updateRecord.status = updateData.status;
        if (updateData.updateBranches)
          updateRecord.branchIds = updateData.branchIds;
        return updateRecord;
      });

      await onSubmit(requestPayload);
    } catch (error) {
      console.error("Error updating office timings:", error);
      toast.error("An error occurred during the update.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-full flex flex-col my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Edit3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Bulk Update Office Timings
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Update {selectedCount} selected office timing
                {selectedCount > 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="updateShiftName"
                      checked={updateData.updateShiftName}
                      onChange={(e) =>
                        handleChange("updateShiftName", e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="updateShiftName"
                      className="font-medium text-gray-900 cursor-pointer"
                    >
                      Update Shift Name
                    </label>
                  </div>
                  {updateData.updateShiftName && (
                    <input
                      type="text"
                      value={updateData.shiftName}
                      onChange={(e) =>
                        handleChange("shiftName", e.target.value)
                      }
                      placeholder="Enter new shift name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="updateStatus"
                      checked={updateData.updateStatus}
                      onChange={(e) =>
                        handleChange("updateStatus", e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="updateStatus"
                      className="font-medium text-gray-900 cursor-pointer"
                    >
                      Update Status
                    </label>
                  </div>
                  {updateData.updateStatus && (
                    <select
                      value={updateData.status}
                      onChange={(e) => handleChange("status", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Status</option>
                      <option value="ENABLED">Enabled</option>
                      <option value="DISABLED">Disabled</option>
                    </select>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="updateTimes"
                    checked={updateData.updateTimes}
                    onChange={(e) =>
                      handleChange("updateTimes", e.target.checked)
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="updateTimes"
                    className="flex items-center gap-2 font-medium text-gray-900 cursor-pointer"
                  >
                    <Clock className="w-4 h-4" />
                    Update Shift Times
                  </label>
                </div>
                {updateData.updateTimes && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={updateData.shiftForm}
                        onChange={(e) =>
                          handleChange("shiftForm", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={updateData.shiftTo}
                        onChange={(e) =>
                          handleChange("shiftTo", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="updateOffTime"
                    checked={updateData.updateOffTime}
                    onChange={(e) =>
                      handleChange("updateOffTime", e.target.checked)
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="updateOffTime"
                    className="font-medium text-gray-900 cursor-pointer"
                  >
                    Update Office Hours
                  </label>
                </div>
                {updateData.updateOffTime && (
                  <div className="max-w-xs">
                    <input
                      type="number"
                      value={updateData.offTime}
                      onChange={(e) => handleChange("offTime", e.target.value)}
                      placeholder="Enter office hours"
                      min="0"
                      step="0.1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="updateBranches"
                    checked={updateData.updateBranches}
                    onChange={(e) =>
                      handleChange("updateBranches", e.target.checked)
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="updateBranches"
                    className="flex items-center gap-2 font-medium text-gray-900 cursor-pointer"
                  >
                    <Building className="w-4 h-4" />
                    Update Branches
                  </label>
                </div>
                {updateData.updateBranches &&
                  (isLoadingBranches ? (
                    <div className="text-center p-4 text-gray-500">
                      Loading branches...
                    </div>
                  ) : branches.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">
                          {updateData.branchIds.length} of {branches.length}{" "}
                          selected
                        </span>
                        <button
                          type="button"
                          onClick={handleSelectAllBranches}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {updateData.selectAllBranches
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {branches.map((branch) => (
                          <button
                            key={branch.branchId}
                            type="button"
                            onClick={() => handleBranchSelect(branch.branchId)}
                            className={`flex items-center gap-3 p-3 border rounded-lg text-left transition-colors ${
                              updateData.branchIds.includes(branch.branchId)
                                ? "border-blue-500 bg-blue-50 text-blue-700"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            {updateData.branchIds.includes(branch.branchId) ? (
                              <CheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-sm font-medium">
                              {branch.branchName}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-4 text-gray-500">
                      No enabled branches found.
                    </div>
                  ))}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="flex items-center gap-2 font-medium text-gray-900 mb-4">
                  <Users className="w-4 h-4" />
                  Selected Office Timings ({selectedCount})
                </h4>
                <div className="space-y-2">
                  {selectedShifts?.slice(0, 3).map((shift) => (
                    <div
                      key={shift.officeTimingId}
                      className="flex items-center justify-between p-3 bg-white rounded-md border"
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-medium text-gray-900">
                          {shift.shiftName}
                        </span>
                        <span className="text-sm text-gray-500 font-mono">
                          {shift.shiftForm} - {shift.shiftTo}
                        </span>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          shift.status === "ENABLED"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {shift.status}
                      </span>
                    </div>
                  ))}
                  {selectedCount > 3 && (
                    <div className="text-center py-2 text-sm text-gray-500">
                      +{selectedCount - 3} more office timings
                    </div>
                  )}
                </div>
              </div>

              {hasUpdates && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">
                      Please review your changes carefully
                    </p>
                    <p>
                      This action will update {selectedCount} office timing
                      records and cannot be undone.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!hasUpdates || isSubmitting}
              className={`px-6 py-2 text-white rounded-md transition-colors ${
                hasUpdates && !isSubmitting
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isSubmitting
                ? "Updating..."
                : `Update ${selectedCount} Record${
                    selectedCount > 1 ? "s" : ""
                  }`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkUpdateModal;
